import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs';

import { CustomSnackbarService } from 'src/app/shared/components/custom-snackbar/custom-snackbar.service';
import { VIEW_TYPES } from 'src/app/shared/utils/constants';
import { setSessionActive } from 'src/app/stores/global';
import { selectSettingFrodoTimeouts } from 'src/app/stores/settings';

import { NavigationService } from '../navigation/navigation.service';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  private idle: Observable<Event> = new Observable();
  private timer: Subscription = new Subscription();
  private timeOutMilliSeconds = 1000;
  private idleSubscription: Subscription = new Subscription();
  private state: RouterStateSnapshot;
  public expired: Subject<boolean> = new Subject<boolean>();
  private associateTimeoutMessage = 'common.associateTimeoutMessage';
  private isIdleServiceInitialized = false;
  private associateTimeout: number;
  private serviceSessionTimeout: number;

  constructor(
    private store: Store,
    private router: Router,
    private customSnackbarService: CustomSnackbarService,
    private navigationService: NavigationService
  ) {
    this.init();
  }

  init(): void {
    this.store.select(selectSettingFrodoTimeouts).subscribe((timeouts) => {
      this.associateTimeout = timeouts.associateTimeout;
      this.serviceSessionTimeout = timeouts.serviceSessionTimeout;
    });
  }
  /**
   * Starts a timer that will redirect Frodo to the front-loading home page if the user does not interact with the screen
   */
  public startTimer() {
    // Create observable with the events that will reset the timer
    this.idle = merge(
      // fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'DOMMouseScroll'),
      fromEvent(document, 'mousewheel'),
      fromEvent(document, 'click'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'touchmove'),
      // fromEvent(document, 'MSPointerMove'),
      // fromEvent(window, 'mousemove'),
      fromEvent(window, 'resize')
    );

    this.state = this.router.routerState.snapshot;
    const currentUrl = this.state.url.split('/')[1];

    // Get the current associateTimeout or serviceSessionTimeout
    this.timeOutMilliSeconds =
      currentUrl === VIEW_TYPES.SETUP ? this.serviceSessionTimeout : this.associateTimeout;

    //This help us checking if a timer is active or not in other components
    this.isIdleServiceInitialized = true;
    // Subscribe to the events' observable. If the user triggers any of those events, the timer is reset.
    this.idleSubscription = this.idle.subscribe(() => {
      this.resetTimer();
    });
    // Subscribe to the timer
    this.timer = timer(this.timeOutMilliSeconds, this.timeOutMilliSeconds).subscribe(() => {
      this.stopTimer();
      this.store.dispatch(setSessionActive({ value: false }));
      this.customSnackbarService.openSnackBar(this.associateTimeoutMessage, 'Warning');
      this.navigationService.navigateToRootRoute();
    });
  }

  /**
   * Function to restart the timer. It should be triggered when a user interacts with FLF.
   */
  private resetTimer() {
    this.timer.unsubscribe();
    this.idleSubscription.unsubscribe();
    this.startTimer();
  }

  /**
   * Function to stop the timer. It should be used in places where a timer is not needed,
   * when the session timedout or when the user navigated to FLF's home screen
   */
  public stopTimer() {
    this.isIdleServiceInitialized = false;
    this.timer.unsubscribe();
    this.idleSubscription.unsubscribe();
  }

  initIdleTimer(): void {
    if (!this.isIdleServiceInitialized) {
      this.startTimer();
    }
  }
}
